import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, User, Menu, X, LogOut, Video, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { isCreator } from '../utils/roleUtils';

const Navigation = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, logout } = useAuth();

  const navItems = [
    { name: 'Accueil', path: '/' },
    { name: 'Films IA', path: '/films' },
    { name: 'Séries IA', path: '/series' },
    { name: 'Nouveautés', path: '/nouveautes' },
    { name: 'Ma Watchlist', path: '/watchlist' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigation vers la page de recherche (sera implémentée plus tard)
      console.log('Recherche:', searchQuery);
      setIsSearchOpen(false);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-lg border-b border-violet-900/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_emergent-ai-stream/artifacts/5inqwv3t_Logo%20IA%20Watch.png" 
              alt="Émergent AI" 
              className="h-8 w-auto"
            />
            <span className="text-white font-bold text-xl bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent">
              YUUME
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`transition-all duration-300 ${
                  isActive(item.path)
                    ? 'text-pink-400 font-semibold glow-pink'
                    : 'text-gray-300 hover:text-white hover:glow-blue'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Search & User Actions */}
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              {isSearchOpen ? (
                <form onSubmit={handleSearchSubmit} className="flex items-center">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-64 px-4 py-2 bg-gray-900/90 border border-violet-500/30 rounded-full text-white placeholder-gray-400 focus:outline-none focus:border-pink-400 transition-colors"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setIsSearchOpen(false)}
                    className="ml-2 text-gray-400 hover:text-white"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </form>
              ) : (
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="text-gray-300 hover:text-white hover:glow-blue transition-all duration-300"
                >
                  <Search className="h-6 w-6" />
                </button>
              )}
            </div>

            {/* User Profile */}
            <div className="relative">
              {user ? (
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-300 hidden sm:block">
                    {userProfile?.username || user.email}
                  </span>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="text-gray-300 hover:text-white hover:glow-blue transition-all duration-300 relative"
                  >
                    <User className="h-6 w-6" />
                  </button>
                  
                  {/* User Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-gray-900/95 backdrop-blur-lg border border-gray-700 rounded-lg shadow-lg z-50">
                      <div className="p-3 border-b border-gray-700">
                        <p className="text-white font-semibold">{userProfile?.username}</p>
                        <p className="text-gray-400 text-sm">{user.email}</p>
                      </div>
                      <div className="py-2">
                        <Link
                          to="/profile"
                          className="flex items-center space-x-2 px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <User className="h-4 w-4" />
                          <span>Mon profil</span>
                        </Link>
                        
                        {/* Liens créateur */}
                        {isCreator(userProfile?.role) && (
                          <>
                            <Link
                              to="/my-videos"
                              className="flex items-center space-x-2 px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <Video className="h-4 w-4" />
                              <span>Mes vidéos</span>
                            </Link>
                            <Link
                              to="/add"
                              className="flex items-center space-x-2 px-3 py-2 text-pink-400 hover:text-pink-300 hover:bg-gray-800 transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <Plus className="h-4 w-4" />
                              <span>Ajouter vidéo</span>
                            </Link>
                          </>
                        )}
                        
                        <button
                          onClick={handleLogout}
                          className="flex items-center space-x-2 w-full px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 transition-colors text-left"
                        >
                          <LogOut className="h-4 w-4" />
                          <span>Se déconnecter</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Link
                    to="/signin"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/signup"
                    className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-full transition-colors font-medium"
                  >
                    S'inscrire
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-gray-300 hover:text-white"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-violet-900/30">
            <div className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`transition-all duration-300 ${
                    isActive(item.path)
                      ? 'text-pink-400 font-semibold'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .glow-pink {
          text-shadow: 0 0 10px rgba(236, 72, 153, 0.5);
        }
        .glow-blue {
          text-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
        }
      `}</style>
    </nav>
  );
};

export default Navigation;